import { useState, useEffect } from 'react';
import { Calculator as CalculatorIcon, Ruler, Search, StickyNote } from 'lucide-react';
import { Calculator } from '../map-tools/calculator';
import { MeasurementTool } from '../map-tools/measurement';
import { AddressSearch } from '../map-tools/address-search';
import { AnnotationsTool } from '../map-tools/annotations';
import { ScreenshotTool } from '../map-tools/screenshot';
import { mapEngine } from '../map-engine/MapEngineService';
import { useModuleStore } from '../module-store';
import { useEditLock } from '../layouts';
// Module Registry - Renderiza toolbars de módulos dinámicamente
import { useModule } from '../module-registry';
import './map-panels.css';

/**
 * BottomToolbar - Toolbar inferior permanente de WESTEER
 *
 * @version 5.0
 * @updated 2026-02-16
 * @module core/map-panels
 *
 * @remarks
 * **IMPORTANTE**: Este toolbar es PERMANENTE y parte del CORE.
 *
 * **Diseño**:
 * - Tamaño fijo (no se expande/contrae con paneles laterales)
 * - Altura: 80px (para dos filas de botones de 32px)
 * - Extremos redondeados como semicírculos completos (border-radius: 40px)
 * - Márgenes fijos: 12px en todos los lados
 * - Glassmorphism intenso
 * - Botones: 32x32px con gap de 8px entre ellos
 *
 * **Organización Dinámica**:
 * - **Izquierda (Sección Modular)**: Herramientas que dependen del módulo activo
 *   - Dos filas de 8 botones cada una (16 botones totales)
 *   - **Environmental**: Muestra botón de árbol + placeholders
 *   - **Otros módulos**: Placeholders (herramientas futuras)
 *   - **Sin módulo**: Placeholders deshabilitados
 * - **Derecha (Sección Elemental)**: Herramientas core independientes
 *   - Primera fila: Medición, Calculadora (2 botones)
 *   - Segunda fila: Búsqueda, Anotaciones, Screenshot (3 botones)
 * - Gap de separación: 16px entre ambas secciones
 *
 * **Comportamiento**:
 * - Las herramientas modulares cambian automáticamente cuando se selecciona un módulo en el TopBar
 * - La sección elemental permanece fija e independiente del módulo activo
 *
 * @example
 * ```tsx
 * <BottomToolbar />
 * ```
 */

export interface BottomToolbarProps {
  /**
   * Callback cuando se activa una herramienta
   * @param toolId - ID de la herramienta activada (null = desactivar)
   * @returns Promise<void> | void - Puede ser async para verificar cambios sin guardar
   */
  onToolChange?: (toolId: string | null) => void | Promise<void>;

  /**
   * ID de la herramienta actualmente activa
   */
  activeTool?: string | null;
}

export function BottomToolbar({ onToolChange, activeTool }: BottomToolbarProps): JSX.Element {
  const [localActiveTool, setLocalActiveTool] = useState<string | null>(activeTool || null);
  const [showCalculator, setShowCalculator] = useState<boolean>(false);
  const [measurementValue, setMeasurementValue] = useState<number | undefined>(undefined);
  const [showAddressSearch, setShowAddressSearch] = useState<boolean>(false);

  // Obtener módulo activo del store
  const activeModule = useModuleStore((state) => state.activeModule);

  // Obtener definición del módulo activo desde el registry
  // Esto permite renderizar dinámicamente el toolbar sin hardcodear módulos
  const activeModuleDefinition = useModule(activeModule);

  // Sistema de bloqueo de edición
  const { isEditingActive, hasUnsavedChanges } = useEditLock();

  /**
   * Sincronizar estado local con prop externa
   */
  useEffect(() => {
    setLocalActiveTool(activeTool || null);
  }, [activeTool]);

  /**
   * Maneja el click en un botón de herramienta
   * Toggle: si ya está activo, lo desactiva
   * Ahora es async para permitir verificación de cambios sin guardar
   */
  const handleToolClick = async (toolId: string): Promise<void> => {
    const newTool = localActiveTool === toolId ? null : toolId;

    // Llamar a onToolChange (puede ser async)
    if (onToolChange) {
      await onToolChange(newTool);
      // Solo actualizar estado local si el cambio fue exitoso
      // (Si onToolChange rechaza el cambio, activeTool externo no cambiará
      // y useEffect sincronizará localActiveTool)
    } else {
      setLocalActiveTool(newTool);
    }
  };

  /**
   * Manejar envío de valor desde medición a calculadora
   */
  const handleSendToCalculator = (value: number): void => {
    setMeasurementValue(value);
    setShowCalculator(true);
  };

  return (
    <>
      <div className="bottom-toolbar" role="toolbar" aria-label="Herramientas de mapa">
        <div className="toolbar-content">
          {/* ===== SECCIÓN MODULAR (IZQUIERDA) ===== */}
          {activeModuleDefinition?.toolbar?.content && (
            <div
              className="toolbar-modular-section"
              style={{
                opacity: isEditingActive && hasUnsavedChanges ? 0.4 : 1,
                pointerEvents: isEditingActive && hasUnsavedChanges ? 'none' : 'auto',
              }}
              title={
                isEditingActive && hasUnsavedChanges
                  ? 'Debe guardar o cancelar los cambios antes de usar herramientas de creación'
                  : undefined
              }
            >
              {/* Renderizar toolbar del módulo registrado dinámicamente */}
              {(() => {
                const ToolbarContent = activeModuleDefinition.toolbar.content;
                return (
                  <ToolbarContent
                    activeTool={localActiveTool}
                    onToolChange={async (toolId) => {
                      // Bloquear cambio de herramienta si hay edición activa
                      if (isEditingActive && hasUnsavedChanges) {
                        alert('Debe guardar o cancelar los cambios antes de usar herramientas de creación.');
                        return;
                      }

                      if (onToolChange) {
                        await onToolChange(toolId);
                      } else {
                        setLocalActiveTool(toolId);
                      }
                    }}
                  />
                );
              })()}
            </div>
          )}

          {/* ===== SECCIÓN ELEMENTAL (DERECHA) ===== */}
          <div className="toolbar-elementary-section">
            {/* Primera fila: Medición, Calculadora */}
            <div className="toolbar-row">
              {/* Herramienta de Medición */}
              <button
                className={`toolbar-square-btn ${localActiveTool === 'measurement' ? 'active' : ''}`}
                aria-label="Herramienta de medición"
                title="Medir distancias y áreas"
                onClick={() => handleToolClick('measurement')}
                disabled={!activeModule}
              >
                <Ruler size={20} />
              </button>

              {/* Calculadora */}
              <button
                className="toolbar-square-btn"
                aria-label="Calculadora"
                title="Abrir calculadora"
                onClick={() => setShowCalculator(true)}
                disabled={!activeModule}
              >
                <CalculatorIcon size={20} />
              </button>
            </div>

            {/* Segunda fila: Búsqueda, Anotaciones, Screenshot */}
            <div className="toolbar-row">
              {/* Búsqueda de Dirección */}
              <button
                className="toolbar-square-btn"
                aria-label="Buscar dirección"
                title="Buscar dirección o lugar"
                onClick={() => setShowAddressSearch(!showAddressSearch)}
                disabled={!activeModule}
              >
                <Search size={20} />
              </button>

              {/* Anotaciones */}
              <button
                className={`toolbar-square-btn ${localActiveTool === 'annotations' ? 'active' : ''}`}
                aria-label="Anotaciones"
                title="Agregar anotaciones en el mapa"
                onClick={() => handleToolClick('annotations')}
                disabled={!activeModule}
              >
                <StickyNote size={20} />
              </button>

              {/* Captura de Pantalla */}
              <ScreenshotTool map={mapEngine.getMap()} disabled={!activeModule} />
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Modal */}
      <Calculator
        isOpen={showCalculator}
        onClose={() => {
          setShowCalculator(false);
          setMeasurementValue(undefined);
        }}
        initialValue={measurementValue}
        onValueInserted={() => setMeasurementValue(undefined)}
      />

      {/* Measurement Tool Panel */}
      <MeasurementTool
        map={mapEngine.getMap()}
        isActive={localActiveTool === 'measurement'}
        onDeactivate={() => handleToolClick('measurement')}
        onSendToCalculator={handleSendToCalculator}
      />

      {/* Address Search Panel */}
      <AddressSearch
        map={mapEngine.getMap()}
        isOpen={showAddressSearch}
        onClose={() => setShowAddressSearch(false)}
      />

      {/* Annotations Tool */}
      <AnnotationsTool
        map={mapEngine.getMap()}
        isActive={localActiveTool === 'annotations'}
        onDeactivate={() => handleToolClick('annotations')}
      />
    </>
  );
}
